import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.tsx';
import { applyAudience } from '../theme/useAudienceTheme.ts';
import { t } from '../i18n/index.ts';
import { PasswordInput } from '../ui/PasswordInput.tsx';
import { api, ApiError } from '../lib/api.ts';
import { solveChallenge } from '../lib/pow.ts';
import { AlignovaMark } from '../ui/AlignovaLogo.tsx';

// The sign-in screen. Two panels on a desktop - a brand panel that carries the mark, and the form -
// and just the form on a phone. Everything is drawn from the theme's brand tokens (--c-brand,
// --c-brand-deep, --c-brand-warm), so this page never spells a hex value the rest of the app
// does not know about.

interface Challenge {
  token: string | null;
  difficulty: number;
}

// Staggered entrance: each block rises in a beat after the one before it. Purely decorative -
// index.css switches the animation off for people who asked their OS for reduced motion.
const rise = (step: number) => ({ animationDelay: `${80 + step * 70}ms` });

export function Login() {
  const { user, loading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [website, setWebsite] = useState(''); // honeypot - humans never see or fill this
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'idle' | 'verifying' | 'signing'>('idle');
  const [challenge, setChallenge] = useState<Challenge | null>(null);

  applyAudience('staff'); // login is a staff-themed public page

  const fetchChallenge = () => {
    api.get<Challenge>('/auth/challenge').then(setChallenge).catch(() => setChallenge({ token: null, difficulty: 0 }));
  };
  useEffect(() => {
    fetchChallenge();
    // Server tokens live 5 minutes; refresh well inside that so an idle page still submits cleanly.
    const id = setInterval(fetchChallenge, 4 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (!loading && user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      let solved: { token: string; solution: string } | undefined;
      if (challenge?.token) {
        setBusy('verifying');
        solved = { token: challenge.token, solution: await solveChallenge(challenge.token, challenge.difficulty) };
      }
      setBusy('signing');
      await api.post('/auth/login', { username, password, website, challenge: solved });
      // Full reload so the auth context re-reads the session cookie and routes by audience.
      window.location.assign('/');
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : t('login.error'));
      setBusy('idle');
      fetchChallenge(); // tokens are single-use
    }
  }

  const inputClass =
    'w-full rounded border border-line bg-surface px-3.5 py-2.5 text-[15px] text-ink placeholder:text-muted outline-none transition-[border-color,box-shadow] duration-200 hover:border-muted focus:border-brand focus:ring-2 focus:ring-brand aria-[invalid=true]:border-status-urgent-fg/60';

  const working = busy !== 'idle';

  return (
    <main className="grid min-h-dvh w-full bg-bg lg:grid-cols-2">
      {/* Brand panel (desktop only). Sticky so it stays put if the form ever needs to scroll. */}
      <aside
        className="grain relative hidden overflow-hidden text-white lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col lg:justify-between lg:p-12 xl:p-14"
        style={{
          backgroundColor: 'var(--c-brand-deep)',
          backgroundImage:
            'radial-gradient(70% 60% at 15% 10%, color-mix(in srgb, var(--c-brand) 90%, white 10%) 0%, transparent 65%),' +
            'radial-gradient(45% 40% at 95% 90%, color-mix(in srgb, var(--c-brand-warm) 32%, transparent) 0%, transparent 70%)',
        }}
      >
        {/* The mark itself, oversized and bleeding off the corner, instead of decorative blobs. */}
        <AlignovaMark
          className="pointer-events-none absolute -bottom-[18%] -right-[14%] h-[78%] w-auto text-white opacity-[0.07]"
          style={{ transform: 'rotate(-8deg)' }}
        />

        <div className="animate-rise relative z-10 flex items-center gap-3" style={rise(0)}>
          <AlignovaMark className="h-9 w-9 text-white" />
          <span className="text-base font-bold tracking-[0.16em] text-white/90">ALIGNOVA</span>
        </div>

        <div className="relative z-10 my-auto max-w-md py-10">
          <p className="animate-rise text-sm font-medium tracking-wide text-brand-warm" style={rise(1)}>
            Clear dental aligners, made in Pakistan
          </p>
          <h2 className="animate-rise mt-4 text-display-lg text-white [text-wrap:balance]" style={rise(2)}>
            Every case, from records to delivery, in one place.
          </h2>
          <div className="animate-rise mt-8 h-px w-16 bg-brand-warm" style={rise(3)} />
          <p className="animate-rise mt-6 max-w-[42ch] text-[15px] leading-relaxed text-white/75 [text-wrap:pretty]" style={rise(4)}>
            The lab console behind Alignova: setup approvals on a real model video, per-tray pricing shown
            before you commit, production checklists, courier tracking and a wear tracker for the patient.
          </p>
        </div>

        <p className="animate-rise relative z-10 text-xs text-white/45" style={rise(5)}>
          Lab staff, clinics and patients each see only what is theirs.
        </p>
      </aside>

      {/* Form panel: one card, centred in its half, with the small print beneath it. */}
      <section className="flex w-full flex-col items-center justify-center px-5 py-10 sm:px-10">
        <div className="w-full max-w-[26rem]">
          <div className="animate-rise mb-8 flex items-center justify-center gap-3 text-brand lg:hidden" style={rise(0)}>
            <AlignovaMark className="h-10 w-10" />
            <span className="text-lg font-bold tracking-[0.16em]">ALIGNOVA</span>
          </div>

          <div className="animate-rise rounded-2xl border border-line bg-surface p-6 shadow-level-2 sm:p-8" style={rise(1)}>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted">
            Alignova lab console
          </p>
          <h1 className="mt-2 text-[2.25rem] font-extrabold leading-none tracking-[-0.03em] text-ink">
            {t('login.title')}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-muted">
            Lab staff and clinic accounts sign in here.
          </p>

          <form onSubmit={onSubmit} className="relative mt-8 space-y-5" autoComplete="on" aria-busy={working}>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink" htmlFor="username">
                {t('login.username')}
              </label>
              <input
                id="username"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={inputClass}
                placeholder="Your username or email"
                aria-invalid={!!error}
                disabled={working}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink" htmlFor="password">
                {t('login.password')}
              </label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                aria-invalid={!!error}
                disabled={working}
              />
            </div>

            {/* Honeypot: off-screen, never focusable; bots that auto-fill it are rejected. */}
            <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden">
              <label htmlFor="website">Website</label>
              <input id="website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>

            {error && (
              <p className="rounded border border-status-urgent-fg/20 bg-status-urgent-bg px-3.5 py-2.5 text-sm text-status-urgent-fg" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={working}
              className="tap inline-flex w-full items-center justify-center gap-2.5 rounded bg-brand py-3 text-[15px] font-semibold text-white shadow-level-2 transition-[background-color,transform,box-shadow] duration-200 hover:bg-brand-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-progress disabled:opacity-70"
            >
              {working && (
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
                  <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
              )}
              {busy === 'verifying' ? 'Checking you are not a bot…' : busy === 'signing' ? 'Signing in…' : t('login.submit')}
            </button>
          </form>
          </div>

          <div className="animate-rise mt-6 space-y-1.5 text-center text-sm text-muted" style={rise(5)}>
            <p>
              New clinic?{' '}
              <Link
                to="/register"
                className="font-semibold text-brand underline decoration-line underline-offset-[3px] transition hover:decoration-brand"
              >
                Register your clinic
              </Link>
            </p>
            <p>Forgotten your password? Ask the lab to reset it for you.</p>
          </div>

          <p className="animate-rise mt-10 text-center text-xs text-muted" style={rise(6)}>
            © {new Date().getFullYear()} Alignova · Clear dental aligners
          </p>
        </div>
      </section>
    </main>
  );
}
